import { useEffect, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://personalinjury-junlawfirm.com';

const immigrationSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/immigration#webpage`,
      url: `${siteUrl}/immigration`,
      name: 'Immigration Attorney Indiana | Fishers Immigration Lawyer | Jun Law Firm',
      description:
        'Dedicated immigration attorney in Fishers, Indiana serving foreign-born individuals. Visa applications, green card petitions, deportation defense, DACA, naturalization. Multilingual team. Free consultation. (317) 701-2768.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/immigration#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Immigration Attorney', item: `${siteUrl}/immigration` },
        ],
      },
    },
    {
      '@type': 'LegalService',
      '@id': `${siteUrl}/immigration#service`,
      name: 'Jun Law Firm - Immigration Attorney Indiana',
      url: `${siteUrl}/immigration`,
      telephone: '+13177012768',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fishers',
        addressRegion: 'IN',
        postalCode: '46037',
        addressCountry: 'US',
      },
      areaServed: { '@type': 'State', name: 'Indiana' },
      description:
        'Experienced immigration attorney in Fishers, Indiana offering visa applications, green card petitions, deportation defense, DACA, naturalization, and family petitions. Multilingual team.',
      priceRange: 'Free consultation',
      availableLanguage: [
        { '@type': 'Language', name: 'English' },
        { '@type': 'Language', name: 'Spanish' },
        { '@type': 'Language', name: 'Korean' },
        { '@type': 'Language', name: 'Chinese' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Immigration Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Visa Application' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Green Card Petition' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Deportation Defense' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'DACA & TPS' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Naturalization' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Family Petitions' } },
        ],
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What immigration services does Jun Law Firm offer in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Jun Law Firm provides comprehensive immigration legal services including visa applications (H-1B, L-1, O-1, F-1), green card petitions, deportation defense, DACA and TPS renewals, naturalization, and family-based petitions for clients throughout Indiana.',
          },
        },
        {
          '@type': 'Question',
          name: 'What should I do if I receive a Notice to Appear (NTA) for removal proceedings?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Contact an immigration attorney immediately. A Notice to Appear initiates formal removal proceedings in immigration court. You have the right to legal representation, and an attorney can contest the charges, identify available relief, and represent you at all hearings.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I apply for asylum if I am already in removal proceedings?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Even in removal proceedings, you can apply for defensive asylum as a form of relief. If granted, asylum stops removal and provides a pathway to a green card and U.S. citizenship. You may also qualify for Withholding of Removal or Convention Against Torture (CAT) protection.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does Jun Law Firm offer multilingual immigration legal services?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Our multilingual team provides immigration legal services in English, Spanish, Korean, and Chinese. Language should never be a barrier to justice.',
          },
        },
      ],
    },
  ],
};

export default function Immigration() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeModal, setActiveModal] = useState<number | null>(null);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [topicHighlight, setTopicHighlight] = useState(false);

  const topicValues = ['visa', 'green_card', 'deportation', 'daca', 'naturalization', 'family'];

  const scrollToContact = (modalIndex?: number) => {
    if (modalIndex !== undefined && modalIndex >= 0 && modalIndex < topicValues.length) {
      setSelectedTopic(topicValues[modalIndex]);
      setTopicHighlight(true);
      setTimeout(() => setTopicHighlight(false), 1800);
    }
    setActiveModal(null);
    setTimeout(() => {
      const element = document.getElementById('contact-form');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  };

  const immigrationServices = [
    {
      number: 'I',
      title: 'Visa Applications',
      description: 'Expert guidance for work visas (H-1B, L-1, O-1), family-based visas, and nonimmigrant visa petitions to maximize approval chances.',
      modalDescription: 'Navigating the U.S. visa system can be overwhelming — but you don\'t have to do it alone. Our team provides comprehensive assistance for a wide range of visa categories including:\n\n• H-1B (Specialty Occupation Workers)\n• L-1 (Intracompany Transferees)\n• O-1 (Individuals with Extraordinary Ability)\n• F-1 (Student Visas)\n• B-1/B-2 (Visitor & Business Visas)\n• Family-Based Nonimmigrant Visas\n\nWe carefully prepare your petition, gather all required supporting documentation, and guide you through every step of the consular or USCIS process. Our goal is to give your application the strongest possible foundation for approval.',
    },
    {
      number: 'II',
      title: 'Green Card Petitions',
      description: 'Comprehensive support for employment-based and family-based permanent residency applications, adjustment of status, and consular processing.',
      modalDescription: 'Obtaining lawful permanent residence is a life-changing milestone. We handle the full spectrum of green card pathways:\n\n• Employment-Based (EB-1, EB-2, EB-3, EB-5)\n• Family-Based (Immediate Relatives & Preference Categories)\n• Adjustment of Status (Form I-485)\n• Consular Processing abroad\n• Removal of Conditions (Form I-751)\n• Refugee/Asylee-based Green Cards\n\nOur attorneys ensure your petition package is complete, accurate, and compelling. We monitor your priority dates, prepare you for interviews, and stay by your side until you receive your green card.',
    },
    {
      number: 'III',
      title: 'Deportation Defense',
      description: 'Aggressive representation before immigration courts, including removal proceedings defense, appeals, and emergency stays of removal.',
      modalDescription: '',
    },
    {
      number: 'IV',
      title: 'DACA & TPS',
      description: 'Assistance with DACA renewals, initial applications, and Temporary Protected Status filings for eligible individuals.',
      modalDescription: 'DACA (Deferred Action for Childhood Arrivals) and Temporary Protected Status (TPS) provide vital protection for hundreds of thousands of immigrants. We assist with:\n\n• DACA Initial Applications & Renewals (Form I-821D)\n• Employment Authorization Document (EAD) Applications\n• TPS Initial Registration & Re-registration\n• Advance Parole Travel Documents\n• Responding to USCIS Requests for Evidence (RFEs)\n• Guidance on combo advance parole and status adjustment options\n\nOur team stays up to date on the latest policy changes affecting DACA and TPS holders to give you the most current and accurate advice.',
    },
    {
      number: 'V',
      title: 'Naturalization',
      description: 'Step-by-step guidance through the U.S. citizenship application process, interview preparation, and civics exam support.',
      modalDescription: 'Becoming a U.S. citizen is one of the most significant milestones of the immigration journey. We make the naturalization process smooth and stress-free:\n\n• Eligibility Assessment (continuous residence, physical presence, good moral character)\n• Form N-400 Preparation & Filing\n• Civics & English Test Preparation\n• Interview Coaching & Mock Interviews\n• Assistance with Disability/Medical Accommodations (Form N-648)\n• Expedited Naturalization for Military Members\n• Post-Naturalization Support (passport applications, name changes)\n\nWe ensure you are fully prepared for every step, from filing to taking the Oath of Allegiance.',
    },
    {
      number: 'VI',
      title: 'Family Petitions',
      description: 'Reuniting families through spousal visas, parent and child sponsorship, and fiancé(e) visa (K-1) applications.',
      modalDescription: 'Family is at the heart of immigration law. We help you reunite with and bring your loved ones to the United States through:\n\n• Spousal Visa (IR-1 / CR-1) & Green Card\n• Fiancé(e) Visa (K-1) Petitions\n• Parent Sponsorship (IR-5)\n• Child & Sibling Petitions (Preference Categories)\n• I-130 Petition for Alien Relative\n• Affidavit of Support (I-864)\n• Consular Interview Preparation\n• Responding to Administrative Processing Delays\n\nFamily separation is painful. Our dedicated team works diligently to keep processing times as short as possible while ensuring your petition is complete and compelling.',
    },
  ];

  const whyChooseUs = [
    { label: 'Multilingual Team', detail: 'English, Spanish, Korean, Chinese — we speak your language' },
    { label: 'Foreign-Born Focused', detail: 'We understand the unique challenges immigrants face in the U.S.' },
    { label: 'Small Business Expertise', detail: 'Experienced in business and employment-based immigration cases' },
    { label: 'Confidential & Safe', detail: 'Your immigration status is never shared without your consent' },
    { label: 'Transparent Fees', detail: 'Clear pricing with no hidden costs or surprises' },
    { label: 'Indiana-Based', detail: 'Local knowledge of Indiana courts and immigration offices' }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Free Consultation',
      description: 'We review your immigration situation and explain your options at no cost'
    },
    {
      number: '02',
      title: 'Case Strategy',
      description: 'We build a personalized plan tailored to your specific immigration goals'
    },
    {
      number: '03',
      title: 'Document Preparation',
      description: 'We gather and prepare all required forms, evidence, and supporting documents'
    },
    {
      number: '04',
      title: 'Filing & Follow-Up',
      description: 'We submit your case and monitor every step until resolution'
    }
  ];

  return (
    <div className="min-h-screen bg-lavender-50">
      <SEOHead
        title="Immigration Attorney Indiana | Fishers Immigration Lawyer | Jun Law Firm"
        description="Dedicated immigration attorney in Fishers, Indiana serving foreign-born individuals. Visa applications, green card petitions, deportation defense, DACA, naturalization. Multilingual team. Free consultation. (317) 701-2768."
        keywords="immigration attorney Indiana, Fishers immigration lawyer, green card attorney Indiana, visa application lawyer, deportation defense attorney, DACA attorney Indiana"
        canonicalPath="/immigration"
        schema={immigrationSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-lavender-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="text-xs tracking-widest text-gray-500 mb-6 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Immigration Law
            </div>
            <h1 className="text-7xl md:text-8xl font-light text-gray-900 mb-8 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
              Immigration <em className="font-light italic">Attorneys</em>
            </h1>
            <div className="w-24 h-px bg-gray-900 mb-8"></div>
            <p className="text-xl text-gray-700 mb-12 leading-relaxed max-w-2xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Dedicated immigration legal services for foreign-born individuals and small business owners in Indiana. We navigate the complex U.S. immigration system so you don't have to.
            </p>
            <div className="flex flex-col sm:flex-row gap-6">
              <button onClick={scrollToContact} className="bg-gray-900 text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>
                Free Consultation
              </button>
              <a href="tel:+13177012768" className="border-2 border-gray-900 text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center justify-center" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>
                (317) 701-2768
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              What We Handle
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Immigration <em className="italic">Services</em>
            </h2>
            <div className="w-16 h-px bg-gray-900"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {immigrationServices.map((service, index) => (
              <article
                key={index}
                className="group cursor-pointer border border-transparent hover:border-gray-200 hover:bg-white transition-all p-6 -m-6"
                onClick={() => setActiveModal(index)}
              >
                <div className="text-6xl font-light text-gray-300 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {service.number}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-3 group-hover:text-gray-600 transition-colors" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {service.title}
                </h3>
                <div className="w-12 h-px bg-gray-300 mb-4"></div>
                <p className="text-sm text-gray-600 leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {service.description}
                </p>
                <span className="text-xs tracking-widest uppercase text-gray-400 group-hover:text-gray-900 transition-colors flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                  Learn More <i className="ri-arrow-right-line"></i>
                </span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Service Modal — Standard (indices 0,1,3,4,5) */}
      {activeModal !== null && activeModal !== 2 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-backdrop-in"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setActiveModal(null)}
        >
          <div
            className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto animate-modal-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between p-10 border-b border-gray-200">
              <div>
                <div className="text-5xl font-light text-gray-200 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {immigrationServices[activeModal].number}
                </div>
                <h3 className="text-3xl font-light text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {immigrationServices[activeModal].title}
                </h3>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="text-gray-400 hover:text-gray-900 transition-colors cursor-pointer mt-1 ml-4 flex-shrink-0 w-8 h-8 flex items-center justify-center"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            {/* Modal Body */}
            <div className="p-10">
              <div className="w-12 h-px bg-gray-900 mb-6"></div>
              <div className="text-sm text-gray-600 leading-loose whitespace-pre-line" style={{ fontFamily: "'Inter', sans-serif" }}>
                {immigrationServices[activeModal].modalDescription}
              </div>
            </div>
            {/* Modal Footer */}
            <div className="px-10 pb-10 pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-6 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Have questions about {immigrationServices[activeModal].title.toLowerCase()}? Our attorneys are ready to review your case — at no cost.
              </p>
              <button
                onClick={() => scrollToContact(activeModal ?? undefined)}
                className="w-full bg-gray-900 text-white py-4 text-xs tracking-widest uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}
              >
                Request for Consultation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deportation Defense Modal — Special (index 2) */}
      {activeModal === 2 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-backdrop-in"
          style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
          onClick={() => setActiveModal(null)}
        >
          <div
            className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-modal-grow"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gray-900 px-12 py-10">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs tracking-widest text-gray-400 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
                    Immigration Court Defense
                  </div>
                  <div className="text-6xl font-light text-gray-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>III</div>
                  <h3 className="text-4xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Deportation <em className="italic">Defense</em>
                  </h3>
                </div>
                <button
                  onClick={() => setActiveModal(null)}
                  className="text-gray-400 hover:text-white transition-colors cursor-pointer w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1 ml-6"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <div className="mt-6 w-16 h-px bg-gray-600"></div>
              <p className="mt-4 text-gray-400 text-sm leading-relaxed max-w-2xl" style={{ fontFamily: "'Crimson Pro', serif", fontSize: '1rem' }}>
                Facing removal from the United States is one of the most serious legal crises an immigrant can experience. Understanding your rights and the structure of removal proceedings is essential. Our attorneys represent you at every stage — from the very first hearing through appeals — with a strategic, aggressive defense.
              </p>
            </div>

            {/* Body */}
            <div className="px-12 py-10 space-y-12">

              {/* Section 1 — Master Hearing */}
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-8 h-8 bg-gray-900 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>01</span>
                  </div>
                  <h4 className="text-2xl font-light text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Master Hearing <em className="italic text-gray-500">(Initial Calendar Hearing)</em>
                  </h4>
                </div>
                <div className="pl-12 space-y-4">
                  <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                    The Master Hearing is the first formal proceeding in Immigration Court after you receive a <strong>Notice to Appear (NTA)</strong>. It is an administrative hearing — not a full trial — but the decisions made here shape everything that follows. An experienced attorney at this stage can completely change the outcome of your case.
                  </p>
                  <div className="border-l-2 border-gray-200 pl-6 space-y-3">
                    <div>
                      <div className="text-xs tracking-wider text-gray-400 uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>What Happens</div>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        The Immigration Judge reads the charges in the NTA. You (or your attorney) admit or deny each factual allegation and charge of removability. The judge also confirms biographical details, determines whether an interpreter is needed, and ensures you have or are seeking legal representation.
                      </p>
                    </div>
                    <div>
                      <div className="text-xs tracking-wider text-gray-400 uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Pleadings &amp; Contesting Removability</div>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        You do not have to admit that you are removable. Our attorneys carefully review every allegation in the NTA for legal defects, improper charges, or factual inaccuracies. Contesting the charges can buy critical time and may even terminate proceedings altogether.
                      </p>
                    </div>
                    <div>
                      <div className="text-xs tracking-wider text-gray-400 uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Designating Relief &amp; Scheduling</div>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        At the Master Hearing, your attorney identifies which forms of relief you will be pursuing — asylum, cancellation of removal, adjustment of status, voluntary departure, etc. — and an Individual Hearing date is set. Strategic early filing of applications at this stage can preserve rights and establish a stronger record.
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 p-5 mt-4">
                    <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <strong className="text-gray-700">Important:</strong> Multiple Master Hearings may occur before an Individual Hearing is scheduled. Each appearance matters — missing a Master Hearing without proper notice can result in an <em>in absentia</em> order of removal being entered against you.
                    </p>
                  </div>
                </div>
              </div>

              <div className="w-full h-px bg-gray-100"></div>

              {/* Section 2 — Individual Hearing */}
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-8 h-8 bg-gray-900 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>02</span>
                  </div>
                  <h4 className="text-2xl font-light text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Individual Hearing <em className="italic text-gray-500">(Merits Hearing)</em>
                  </h4>
                </div>
                <div className="pl-12 space-y-4">
                  <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                    The Individual Hearing is the full merits trial of your removal case. This is where your legal fate is decided. The judge hears testimony, reviews documentary evidence, and issues a decision — either granting relief and allowing you to remain in the U.S., or ordering removal. Thorough preparation with a skilled attorney is absolutely critical.
                  </p>
                  <div className="border-l-2 border-gray-200 pl-6 space-y-3">
                    <div>
                      <div className="text-xs tracking-wider text-gray-400 uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Testimony &amp; Cross-Examination</div>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        You will testify under oath about your background, the basis for your application, and any fears or hardships you face. The government's attorney (DHS counsel) will cross-examine you. We conduct extensive mock hearings to ensure you are fully prepared, confident, and consistent.
                      </p>
                    </div>
                    <div>
                      <div className="text-xs tracking-wider text-gray-400 uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Documentary Evidence</div>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        We build a comprehensive evidentiary package: country condition reports, police reports, medical records, affidavits, photographs, expert testimony, news articles, and any other documentation supporting your claim. A well-built record is the foundation of every winning case.
                      </p>
                    </div>
                    <div>
                      <div className="text-xs tracking-wider text-gray-400 uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>The Judge's Decision &amp; Next Steps</div>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        The judge may issue a decision from the bench (same day) or in writing. If relief is denied, you have the right to appeal to the <strong>Board of Immigration Appeals (BIA)</strong> within 30 days. We evaluate every case for appeal and continue fighting for you at every level, including federal courts if necessary.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full h-px bg-gray-100"></div>

              {/* Section 3 — Asylum as Relief */}
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-8 h-8 bg-gray-900 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>03</span>
                  </div>
                  <h4 className="text-2xl font-light text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Asylum as a Relief <em className="italic text-gray-500">During Removal Proceedings</em>
                  </h4>
                </div>
                <div className="pl-12 space-y-4">
                  <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Even if you are in removal proceedings, you can apply for asylum as a form of relief — known as <strong>Defensive Asylum</strong>. This is one of the most powerful protections available and, if granted, not only stops removal but provides a pathway to a green card and ultimately U.S. citizenship.
                  </p>
                  <div className="border-l-2 border-gray-200 pl-6 space-y-3">
                    <div>
                      <div className="text-xs tracking-wider text-gray-400 uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Grounds for Asylum</div>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        To qualify, you must demonstrate a well-founded fear of persecution in your home country based on one of five protected grounds: <strong>race, religion, nationality, membership in a particular social group, or political opinion</strong>. The "particular social group" category has been the subject of extensive litigation, and our attorneys stay current on the latest case law to craft the strongest possible claim.
                      </p>
                    </div>
                    <div>
                      <div className="text-xs tracking-wider text-gray-400 uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Withholding of Removal &amp; CAT</div>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        If you are ineligible for asylum (e.g., due to the one-year filing deadline), you may still qualify for <strong>Withholding of Removal</strong> or protection under the <strong>Convention Against Torture (CAT)</strong>. These alternative forms of protection apply a higher standard of proof but can still prevent your removal to a country where you face serious harm or torture.
                      </p>
                    </div>
                    <div>
                      <div className="text-xs tracking-wider text-gray-400 uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Credibility &amp; Country Conditions</div>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Asylum cases hinge on credibility. We work closely with you to document your personal history, corroborate your testimony with independent evidence, and present detailed country condition reports from the U.S. Department of State and reputable human rights organizations. Our attorneys have handled cases from dozens of countries across Latin America, Asia, Africa, and the Middle East.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    {[
                      { label: 'Asylum', detail: 'Granted if persecution proven on a protected ground' },
                      { label: 'Withholding', detail: 'Available even past the 1-year deadline; higher proof standard' },
                      { label: 'CAT Protection', detail: 'Applies when torture by or with government acquiescence is likely' },
                    ].map((item, i) => (
                      <div key={i} className="border border-gray-200 p-5">
                        <div className="text-xs tracking-wider text-gray-400 uppercase mb-2" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.12em' }}>{item.label}</div>
                        <p className="text-xs text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{item.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>

            {/* Footer CTA */}
            <div className="px-12 py-8 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1" style={{ fontFamily: "'Crimson Pro', serif", fontSize: '1.05rem' }}>
                    Time is critical in deportation cases.
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                    If you or a loved one has received a Notice to Appear or is in immigration detention, contact us immediately for a free, confidential consultation.
                  </p>
                </div>
                <button
                  onClick={() => scrollToContact(2)}
                  className="md:flex-shrink-0 bg-gray-900 text-white px-10 py-4 text-xs tracking-widest uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}
                >
                  Request for Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Why Choose Us */}
      <section className="py-24 bg-lavender-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
                Why Us
              </div>
              <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Built for <em className="italic">Foreign-Borns</em>
              </h2>
              <div className="w-16 h-px bg-gray-900 mb-8"></div>
              <p className="text-lg text-gray-700 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Attorney Hong-Min Jun is himself a foreign-born individual who understands firsthand the obstacles immigrants face navigating the American legal system. This isn't just a practice area — it's personal.
              </p>
            </div>
            <div className="space-y-8">
              {whyChooseUs.map((item, index) => (
                <article key={index} className="border-l-2 border-gray-900 pl-6 hover:border-gray-500 transition-colors">
                  <div className="text-xs tracking-widest text-gray-500 mb-2 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <h3 className="text-xl font-light text-gray-900 mb-2" style={{ fontFamily: "'Crimson Pro', serif" }}>
                    {item.label}
                  </h3>
                  <p className="text-sm text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {item.detail}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-gray-900 text-white border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="text-xs tracking-widest text-gray-400 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              How We Work
            </div>
            <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Our <em className="italic">Process</em>
            </h2>
            <div className="w-16 h-px bg-white mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {processSteps.map((step, index) => (
              <article key={index} className="text-center">
                <div className="text-7xl font-light text-gray-700 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {step.number}
                </div>
                <h3 className="text-xl font-light mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {step.title}
                </h3>
                <div className="w-12 h-px bg-gray-700 mx-auto mb-4"></div>
                <p className="text-sm text-gray-400 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {step.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Language Support Banner */}
      <section className="py-16 bg-lavender-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t-4 border-b-4 border-gray-900 py-10 text-center">
            <h3 className="text-4xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              We Speak Your Language
            </h3>
            <p className="text-base text-gray-600 mb-8 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Language should never be a barrier to justice. Our multilingual team is here to help you in your native language.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {['English', 'Español', '한국어', '中文'].map((lang, idx) => (
                <span key={idx} className="text-xs tracking-wider uppercase border border-gray-400 text-gray-700 px-4 py-2">
                  {lang}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Contact Us
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Free <em className="italic">Consultation</em>
            </h2>
            <div className="w-16 h-px bg-gray-900 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Tell us about your immigration situation — we're here to help.
            </p>
          </div>
          <div className="bg-white border border-gray-200 p-12">
            <form
              className="space-y-8"
              data-readdy-form
              id="immigration-consultation"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const data = new URLSearchParams(new FormData(form) as never);
                try {
                  const res = await fetch('https://readdy.ai/api/form/d74o8jivu4krcbn3b9t0', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: data.toString()
                  });
                  if (res.ok) {
                    form.reset();
                    setSelectedTopic('');
                    const msg = document.getElementById('immigration-form-success');
                    if (msg) { msg.classList.remove('hidden'); setTimeout(() => msg.classList.add('hidden'), 5000); }
                  }
                } catch (_) { /* silent */ }
              }}
            >
              <div id="immigration-form-success" className="hidden bg-gray-900 text-white text-center py-4 text-sm tracking-wider uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                Thank you — we will contact you shortly.
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Full Name *</label>
                  <input type="text" name="name" required className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-base bg-transparent" style={{ fontFamily: "'Crimson Pro', serif" }} placeholder="Your full name" />
                </div>
                <div>
                  <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Phone Number *</label>
                  <input type="tel" name="phone" required className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-base bg-transparent" style={{ fontFamily: "'Crimson Pro', serif" }} placeholder="(317) 555-0123" />
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Email Address *</label>
                <input type="email" name="email" required className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-base bg-transparent" style={{ fontFamily: "'Crimson Pro', serif" }} placeholder="your.email@example.com" />
              </div>
              <div>
                <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Immigration Topic</label>
                <div className="relative">
                  <select
                    name="immigration_topic"
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className={`w-full px-0 py-3 border-0 border-b-2 focus:ring-0 outline-none transition-all duration-500 text-base bg-transparent cursor-pointer ${
                      topicHighlight
                        ? 'border-gray-900 text-gray-900'
                        : 'border-gray-300 focus:border-gray-900'
                    }`}
                    style={{ fontFamily: "'Crimson Pro', serif" }}
                  >
                    <option value="">Select a topic</option>
                    <option value="visa">Visa Application</option>
                    <option value="green_card">Green Card / Permanent Residency</option>
                    <option value="deportation">Deportation Defense</option>
                    <option value="daca">DACA / TPS</option>
                    <option value="naturalization">Naturalization / Citizenship</option>
                    <option value="family">Family Petition</option>
                    <option value="other">Other</option>
                  </select>
                  {topicHighlight && (
                    <span
                      className="absolute right-0 top-1/2 -translate-y-1/2 text-xs tracking-wider text-gray-500 pointer-events-none animate-fade-in-up"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Auto-selected ✓
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Your Situation *</label>
                <textarea name="message" required maxLength={500} rows={6} className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all resize-none text-base bg-transparent" style={{ fontFamily: "'Crimson Pro', serif" }} placeholder="Please describe your immigration situation and what kind of help you need... (Max 500 characters)"></textarea>
              </div>
              <button type="submit" className="w-full bg-gray-900 text-white py-5 text-sm tracking-widest uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                Request Free Consultation
              </button>
              <p className="text-xs text-gray-500 text-center leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Your information is completely confidential. Your immigration status will never be disclosed without your consent.
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
